import {isNil} from 'ramda';

import {Base} from '../base/Base';
import type {ViewPartVariants} from '../CarDiagram.types';
import {FrontElectricEngine} from '../parts/electric/FrontElectricEngine';
import {RearElectricEngine} from '../parts/electric/RearElectricEngine';
import {TractionBattery} from '../parts/electric/TractionBattery';
import {TractionBatteryCables} from '../parts/electric/TractionBatteryCables';
import {FrontBumper} from '../parts/exterior/FrontBumper';
import {FrontLeftHeadlight} from '../parts/exterior/FrontLeftHeadlight';
import {FrontRightHeadlight} from '../parts/exterior/FrontRightHeadlight';
import {Grille} from '../parts/exterior/Grille';
import {RearBumper} from '../parts/exterior/RearBumper';
import {RearLeftTaillight} from '../parts/exterior/RearLeftTaillight';
import {RearRightTaillight} from '../parts/exterior/RearRightTaillight';
import {TowBar} from '../parts/exterior/TowBar';
import {AntiRollBar} from '../parts/mechanics/AntiRollBar';
import {FrontLeftBrake} from '../parts/mechanics/FrontLeftBrake';
import {FrontLeftShockAbsorber} from '../parts/mechanics/FrontLeftShockAbsorber';
import {FrontLeftSideshaft} from '../parts/mechanics/FrontLeftSideshaft';
import {FrontLeftUpperControlArm} from '../parts/mechanics/FrontLeftUpperControlArm';
import {FrontLeftWheel} from '../parts/mechanics/FrontLeftWheel';
import {FrontRightBrake} from '../parts/mechanics/FrontRightBrake';
import {FrontRightShockAbsorber} from '../parts/mechanics/FrontRightShockAbsorber';
import {FrontRightSideshaft} from '../parts/mechanics/FrontRightSideshaft';
import {FrontRightUpperControlArm} from '../parts/mechanics/FrontRightUpperControlArm';
import {FrontRightWheel} from '../parts/mechanics/FrontRightWheel';
import {RearLeftBrake} from '../parts/mechanics/RearLeftBrake';
import {RearLeftShockAbsorber} from '../parts/mechanics/RearLeftShockAbsorber';
import {RearLeftSideshaft} from '../parts/mechanics/RearLeftSideshaft';
import {RearLeftUpperControlArm} from '../parts/mechanics/RearLeftUpperControlArm';
import {RearLeftWheel} from '../parts/mechanics/RearLeftWheel';
import {RearRightBrake} from '../parts/mechanics/RearRightBrake';
import {RearRightShockAbsorber} from '../parts/mechanics/RearRightShockAbsorber';
import {RearRightSideshaft} from '../parts/mechanics/RearRightSideshaft';
import {RearRightUpperControlArm} from '../parts/mechanics/RearRightUpperControlArm';
import {RearRightWheel} from '../parts/mechanics/RearRightWheel';
import {SteeringBar} from '../parts/mechanics/SteeringBar';
import {TorsionBar} from '../parts/mechanics/TorsionBar';

interface ElectricViewProps {
  readonly partVariants: ViewPartVariants;
  readonly onPartClick?: (partId: string) => void;
}

export function ElectricView(props: ElectricViewProps) {
  const onPartClick = props.onPartClick;
  const clickable = (partId: string): (() => void) | undefined => {
    if (isNil(onPartClick)) {
      return undefined;
    }
    return () => onPartClick(partId);
  };

  return (
    <div className="relative h-[502px] w-[1342px]">
      <Base />
      {/* SHARED MECHANICS - background */}
      <FrontBumper />
      <FrontRightHeadlight />
      <FrontLeftHeadlight />
      <Grille />
      <RearBumper />
      <RearRightTaillight />
      <RearLeftTaillight />
      <TowBar />
      <SteeringBar />
      <FrontRightWheel />
      <FrontRightBrake />
      <FrontLeftWheel />
      <FrontLeftBrake />
      <FrontRightUpperControlArm />
      <FrontLeftUpperControlArm />
      <FrontRightSideshaft />
      <FrontLeftSideshaft />
      <FrontRightShockAbsorber />
      <FrontLeftShockAbsorber />
      <TorsionBar />
      <RearRightWheel />
      <RearLeftWheel />
      <RearRightBrake />
      <RearLeftBrake />
      <RearRightUpperControlArm />
      <RearLeftUpperControlArm />
      <RearRightSideshaft />
      <RearLeftSideshaft />
      <RearRightShockAbsorber />
      <RearLeftShockAbsorber />
      {/* ELECTRIC SPECIFIC PARTS */}
      <TractionBatteryCables
        variant={props.partVariants['traction-battery-cables'] ?? 'default'}
        onClick={clickable('traction-battery-cables')}
      />
      <FrontElectricEngine
        variant={props.partVariants['front-electric-engine'] ?? 'default'}
        onClick={clickable('front-electric-engine')}
      />
      <RearElectricEngine
        variant={props.partVariants['rear-electric-engine'] ?? 'default'}
        onClick={clickable('rear-electric-engine')}
      />
      <TractionBattery
        variant={props.partVariants['traction-battery'] ?? 'default'}
        onClick={clickable('traction-battery')}
      />
      {/* SHARED MECHANICS */}
      <AntiRollBar />
    </div>
  );
}
